import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getUserInfoWithToken } from '../../../untidy/redux/actions/actions';
import { userInfoRequestId } from '../../../untidy/redux/selectors';
import {
  clearLegacyAuthTokensFromStorage,
  hasLegacyAuthTokensInStorage,
} from '@upright/ui/common/generic/utilities/storage';
import { refreshAccessToken } from '@upright/ui/api/common/fetcher-core';

/**
 * Hook that triggers fetching of own info to the redux store on mount.
 *
 * PLT-4720: previously this hook also (a) lifted JWTs off the SSO redirect
 * URL into `localStorage` and (b) gated the user-info fetch on
 * `getAccessToken()`. Both responsibilities are gone:
 *
 *   (a) SSO redirects now set the `up-access` / `up-refresh` cookies via
 *       `Set-Cookie` and redirect to the bare URL — no `?accessToken=…`
 *       parameter to lift. The handler in
 *       `auth/sso/oidc/after-sso/handle-open-id-auth-code-flow-login-redirect.ts`
 *       was updated in the same PR.
 *
 *   (b) The JWT is no longer readable from JS, so the gate moves to the
 *       server: we always dispatch the user-info fetch, and the saga's
 *       handler treats 401 as "anonymous".
 *
 * The hook does one transitional thing during the rollout window: if the
 * browser still has legacy localStorage tokens from before the cookie
 * migration, hand the refresh token off to the server's
 * `/api/refresh-access-token` endpoint once. The server validates it,
 * issues fresh `up-access` and `up-refresh` cookies, and the legacy
 * localStorage keys are then purged. This keeps existing sessions logged
 * in across the deploy. A follow-up PR removes the migration block.
 */
export function useLoadOwnInfo() {
  const dispatch = useDispatch();

  useEffect(() => {
    let cancelled = false;
    async function migrateThenLoad() {
      if (hasLegacyAuthTokensInStorage()) {
        try {
          await refreshAccessToken();
        } catch {
          // If the legacy refresh token is invalid (expired, revoked) the
          // user just becomes anonymous and the saga will not see them as
          // logged in. Either way, drop the leftover keys.
        }
        clearLegacyAuthTokensFromStorage();
      }
      if (cancelled) return;
      // Action argument is preserved for backward compatibility with the
      // existing saga signature; the saga no longer needs a token now that
      // auth travels as a cookie. PLT-4720.
      dispatch(getUserInfoWithToken(userInfoRequestId));
    }
    void migrateThenLoad();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
